import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { cn, dirtyRing } from "@/lib/utils";

/** Expenses tab: the Client's card-spend settings (GET + PATCH /settings/expenses), applied to every organisation
 * below it. Receipt matching is tuning most Clients never touch. */

const NUMERIC_FIELDS = [
  "matchScoreThreshold",
  "matchScoreMargin",
  "matchAmountToleranceAbsolute",
  "matchAmountTolerancePercent",
  "matchDateWindowDays",
  "backfillWindowDays",
] as const;

type NumericField = (typeof NUMERIC_FIELDS)[number];
type Draft = Record<NumericField, string>;

const EMPTY_DRAFT: Draft = {
  matchScoreThreshold: "",
  matchScoreMargin: "",
  matchAmountToleranceAbsolute: "",
  matchAmountTolerancePercent: "",
  matchDateWindowDays: "",
  backfillWindowDays: "",
};

// Mirrors server validation for inline messages; the API rejects out-of-range values rather than clamping.
const RANGES: Record<NumericField, { min: number; max: number; label: string }> = {
  matchScoreThreshold: { min: 0, max: 1, label: "Match confidence" },
  matchScoreMargin: { min: 0, max: 1, label: "Clear winner margin" },
  matchAmountToleranceAbsolute: { min: 0, max: Number.MAX_SAFE_INTEGER, label: "Amount tolerance" },
  matchAmountTolerancePercent: { min: 0, max: 1, label: "Amount tolerance (proportion)" },
  matchDateWindowDays: { min: 0, max: 90, label: "Date window" },
  backfillWindowDays: { min: 0, max: 365, label: "History imported on connect" },
};

export function ExpensesSettingsSection() {
  const { activeClient, activeClientId } = useAuth();
  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";

  const [requireReview, setRequireReview] = useState(true);
  const [postAsDraft, setPostAsDraft] = useState(true);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  const [saved, setSaved] = useState({ requireReview: true, postAsDraft: true, draft: EMPTY_DRAFT });
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: apiError } = await api.GET("/v1/settings/expenses", {});
      if (cancelled) return;
      if (apiError || !data) {
        setError("Failed to load expense settings.");
        setLoading(false);
        return;
      }
      const next: Draft = {
        matchScoreThreshold: String(data.matchScoreThreshold ?? ""),
        matchScoreMargin: String(data.matchScoreMargin ?? ""),
        matchAmountToleranceAbsolute: String(data.matchAmountToleranceAbsolute ?? ""),
        matchAmountTolerancePercent: String(data.matchAmountTolerancePercent ?? ""),
        matchDateWindowDays: String(data.matchDateWindowDays ?? ""),
        backfillWindowDays: String(data.backfillWindowDays ?? ""),
      };
      // Both gates default ON server-side; the generated type widens them to optional, so mirror the
      // server's default rather than letting undefined read as "off".
      const review = data.requireReviewBeforePost ?? true;
      const draftPosting = data.postAsDraft ?? true;
      setRequireReview(review);
      setPostAsDraft(draftPosting);
      setDraft(next);
      setSaved({ requireReview: review, postAsDraft: draftPosting, draft: next });
      setLoaded(true);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeClientId]);

  const dirty =
    requireReview !== saved.requireReview ||
    postAsDraft !== saved.postAsDraft ||
    NUMERIC_FIELDS.some((f) => draft[f].trim() !== saved.draft[f]);

  function reset() {
    setRequireReview(saved.requireReview);
    setPostAsDraft(saved.postAsDraft);
    setDraft(saved.draft);
    setError(null);
  }

  async function save() {
    if (!dirty) return;

    // Empty means "leave alone", not zero: Number("") is 0, and a zero threshold or margin auto-matches everything.
    const body: Record<string, unknown> = { requireReviewBeforePost: requireReview, postAsDraft };

    for (const field of NUMERIC_FIELDS) {
      const raw = draft[field].trim();
      if (!raw) continue;
      const value = Number(raw);
      if (!Number.isFinite(value)) {
        setError(`${RANGES[field].label} must be a number.`);
        return;
      }
      const { min, max } = RANGES[field];
      if (value < min || value > max) {
        setError(
          max === Number.MAX_SAFE_INTEGER
            ? `${RANGES[field].label} must be ${min} or greater.`
            : `${RANGES[field].label} must be between ${min} and ${max}.`,
        );
        return;
      }
      body[field] = value;
    }

    setSaving(true);
    setError(null);
    const { data, error: apiError } = await api.PATCH("/v1/settings/expenses", { body });
    setSaving(false);
    if (apiError || !data) {
      setError((apiError as { detail?: string } | undefined)?.detail ?? "Failed to save changes.");
      return;
    }

    const next: Draft = {
      matchScoreThreshold: String(data.matchScoreThreshold ?? ""),
      matchScoreMargin: String(data.matchScoreMargin ?? ""),
      matchAmountToleranceAbsolute: String(data.matchAmountToleranceAbsolute ?? ""),
      matchAmountTolerancePercent: String(data.matchAmountTolerancePercent ?? ""),
      matchDateWindowDays: String(data.matchDateWindowDays ?? ""),
      backfillWindowDays: String(data.backfillWindowDays ?? ""),
    };
    const review = data.requireReviewBeforePost ?? true;
    const draftPosting = data.postAsDraft ?? true;
    setRequireReview(review);
    setPostAsDraft(draftPosting);
    setDraft(next);
    setSaved({ requireReview: review, postAsDraft: draftPosting, draft: next });
    toast.success("Expense settings updated");
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading expense settings…
        </CardContent>
      </Card>
    );
  }

  if (!loaded) {
    return (
      <Card>
        <CardContent className="py-12 text-sm text-destructive">
          {error ?? "Unable to load expense settings."}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>How expenses post</CardTitle>
          <CardDescription>
            What happens to card spend once it has been coded. Applies to every organisation under the
            client.
            {!isAdmin && " You need Admin or Owner role to edit these."}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <Label htmlFor="require-review">Review before posting</Label>
              <p className="text-xs text-muted-foreground">
                Coded expenses wait for someone to release them. Turn this off and they post
                automatically once the card transaction settles.
              </p>
            </div>
            <Switch
              id="require-review"
              checked={requireReview}
              disabled={!isAdmin || saving}
              onCheckedChange={setRequireReview}
              className={cn(requireReview !== saved.requireReview && dirtyRing)}
            />
          </div>

          <div className="flex items-center justify-between gap-4 border-t pt-5">
            <div className="flex flex-col gap-1">
              <Label htmlFor="post-as-draft">Post as draft</Label>
              <p className="text-xs text-muted-foreground">
                Expenses arrive in the accounting platform as drafts for a final look, rather than as
                posted transactions.
              </p>
            </div>
            <Switch
              id="post-as-draft"
              checked={postAsDraft}
              disabled={!isAdmin || saving}
              onCheckedChange={setPostAsDraft}
              className={cn(postAsDraft !== saved.postAsDraft && dirtyRing)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Receipt matching</CardTitle>
          <CardDescription>
            When Dolfin pairs a receipt with a card transaction on its own, and when it asks instead. The
            defaults suit most clients — loosen them if too much lands in the review queue, tighten them
            if the wrong receipts are being matched.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <NumberField
            id="matchScoreThreshold"
            label="Match confidence"
            hint="How sure Dolfin must be before pairing a receipt on its own. 0 to 1."
            step="0.01"
            value={draft.matchScoreThreshold}
            savedValue={saved.draft.matchScoreThreshold}
            disabled={!isAdmin || saving}
            onChange={(v) => setDraft((d) => ({ ...d, matchScoreThreshold: v }))}
          />
          <NumberField
            id="matchScoreMargin"
            label="Clear winner margin"
            hint="How far ahead the best candidate must be. Close calls are handed over rather than guessed."
            step="0.01"
            value={draft.matchScoreMargin}
            savedValue={saved.draft.matchScoreMargin}
            disabled={!isAdmin || saving}
            onChange={(v) => setDraft((d) => ({ ...d, matchScoreMargin: v }))}
          />
          <NumberField
            id="matchAmountToleranceAbsolute"
            label="Amount tolerance"
            hint="How far the amounts may differ, as a fixed amount. Covers rounding and small currency drift."
            step="0.01"
            value={draft.matchAmountToleranceAbsolute}
            savedValue={saved.draft.matchAmountToleranceAbsolute}
            disabled={!isAdmin || saving}
            onChange={(v) => setDraft((d) => ({ ...d, matchAmountToleranceAbsolute: v }))}
          />
          <NumberField
            id="matchAmountTolerancePercent"
            label="Amount tolerance (proportion)"
            hint="The same allowance as a proportion, whichever is larger. This is what absorbs tips and service charges. 0 to 1."
            step="0.01"
            value={draft.matchAmountTolerancePercent}
            savedValue={saved.draft.matchAmountTolerancePercent}
            disabled={!isAdmin || saving}
            onChange={(v) => setDraft((d) => ({ ...d, matchAmountTolerancePercent: v }))}
          />
          <NumberField
            id="matchDateWindowDays"
            label="Date window (days)"
            hint="How many days either side of a transaction a receipt may be dated. Up to 90."
            step="1"
            value={draft.matchDateWindowDays}
            savedValue={saved.draft.matchDateWindowDays}
            disabled={!isAdmin || saving}
            onChange={(v) => setDraft((d) => ({ ...d, matchDateWindowDays: v }))}
          />
          <NumberField
            id="backfillWindowDays"
            label="History imported on connect (days)"
            hint="How far back transactions are pulled when a card is first linked. Up to 365."
            step="1"
            value={draft.backfillWindowDays}
            savedValue={saved.draft.backfillWindowDays}
            disabled={!isAdmin || saving}
            onChange={(v) => setDraft((d) => ({ ...d, backfillWindowDays: v }))}
          />
        </CardContent>
      </Card>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {isAdmin && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={reset} disabled={!dirty || saving}>
            Reset
          </Button>
          <Button onClick={save} disabled={!dirty || saving}>
            {saving && <Spinner className="size-4" />}
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}

function NumberField({
  id,
  label,
  hint,
  step,
  value,
  savedValue,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  step: string;
  value: string;
  savedValue: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(value.trim() !== savedValue && dirtyRing)}
      />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
